// THE BUTTON THAT PROMISES TO FIX IT — and what it is allowed to touch.
//
// The person reading an error page is not going to open devtools and clear site
// data. They are going to press the biggest thing on the screen, once, and
// expect to be back at work. So the biggest thing on the screen has to actually
// perform the recovery that a support call would otherwise have to talk them
// through: throw away everything this browser has remembered about the app, and
// load a fresh document from the server.
//
// ⚠️ IT MUST NOT SIGN THEM OUT. That is the one outcome that turns a recovered
// session into a support ticket — an AM who was merely looking at a broken page
// is now staring at a sign-in screen, and the Slack sign-in link is minutes of
// round trip away. So the session cookie is the one thing this is not permitted
// to take.
//
// AND IT IS SAFE BY CONSTRUCTION, NOT BY CAREFULNESS. Auth.js sets every cookie
// it owns — session token, csrf, callback-url — httpOnly, which means
// `document.cookie` cannot see them and therefore cannot expire them. A script
// in this browser physically cannot log the user out by clearing cookies. The
// name check below is belt-and-braces for the day somebody turns httpOnly off;
// the real guarantee is the flag, and it lives in Auth.js's defaults.
//
// WHAT IS ACTUALLY IN HERE TODAY is small — a persona lens cookie
// (lib/persona-cookie.ts, deliberately not httpOnly so the server can read it),
// one cosmetic localStorage key (components/video-stage.tsx), and the
// stale-build reload guard in sessionStorage. None of that is precious, and one
// of them — a lens pointing at a persona that no longer resolves — is exactly
// the kind of corrupt state that produces a render crash on every load until
// something clears it. That is the case this button is for.
//
// ⚠️ NOTHING HERE MAY THROW, AND NOTHING HERE MAY HANG. Every caller is already
// on a broken page, and the failure mode that matters is a button that does
// nothing when pressed — a user who has been promised a fix and gets a spinner
// forever is worse off than one who was told to reload. So every step is
// individually swallowed, and the async half races a deadline it cannot miss.

/** Cookies whose names mean "session", whatever the httpOnly flag says. */
const AUTH_COOKIE_HINTS = ["authjs", "next-auth", "session-token"];

/**
 * How long the whole wipe gets before we navigate anyway.
 *
 * Cache Storage and IndexedDB are async and, worse, can block indefinitely — an
 * IndexedDB delete waits on other tabs holding the database open, and it will
 * wait forever without ever rejecting. Navigating with the wipe half-done is a
 * strictly better outcome than a button that never returns, and the storage we
 * failed to clear is storage the app does not use anyway.
 */
export const RESET_BUDGET_MS = 1500;

const isAuthCookie = (name: string): boolean => {
  const n = name.toLowerCase();
  return AUTH_COOKIE_HINTS.some((hint) => n.includes(hint));
};

/** Swallow anything. See the header — a recovery path may not have its own bugs. */
const attempt = (fn: () => void): void => {
  try {
    fn();
  } catch {
    /* storage disabled, private browsing, a locked database — press on */
  }
};

export interface CookieJar {
  read: () => string;
  write: (value: string) => void;
}

export interface ResetSurfaces {
  local?: Pick<Storage, "clear"> | null;
  session?: Pick<Storage, "clear"> | null;
  cookies?: CookieJar | null;
  /** Where the cookie paths come from; only the pathname is read. */
  pathname?: string;
  caches?: Pick<CacheStorage, "keys" | "delete"> | null;
  // `readonly`, because that is what the DOM's own ServiceWorkerContainer
  // returns and a mutable array here would refuse to accept the real thing.
  serviceWorker?: { getRegistrations: () => Promise<readonly { unregister: () => Promise<boolean> }[]> } | null;
  indexedDB?: IDBFactory | null;
}

/**
 * The paths a cookie could have been scoped to, most specific first.
 *
 * ⚠️ A COOKIE CAN ONLY BE EXPIRED FROM THE PATH IT WAS SET ON, and
 * `document.cookie` hands back names and values with no path attached — so the
 * path has to be guessed, and the only defensible guesses are the current
 * page's own path and its ancestors. Everything this app writes uses `path=/`,
 * which is the last entry and the one that does the work; the rest are there so
 * that a cookie written from some deeper route later does not quietly survive
 * the one button whose entire promise is that it clears things.
 */
export function cookiePathsFor(pathname: string): string[] {
  const segments = pathname.split("/").filter(Boolean);
  const paths: string[] = [];
  for (let i = segments.length; i > 0; i--) paths.push(`/${segments.slice(0, i).join("/")}`);
  paths.push("/");
  return paths;
}

/**
 * Expire every cookie this browser will admit to having, except the session.
 *
 * Returns the names it expired, which is what the tests assert on — the
 * alternative is asserting on a `document.cookie` that jsdom does not actually
 * re-read after an expiry write.
 */
export function clearVisibleCookies(jar: CookieJar, pathname = "/"): string[] {
  const cleared: string[] = [];
  attempt(() => {
    const paths = cookiePathsFor(pathname);
    for (const pair of jar.read().split(";")) {
      const name = pair.split("=")[0]?.trim();
      if (!name || isAuthCookie(name)) continue;
      cleared.push(name);
      for (const path of paths) {
        jar.write(`${name}=; path=${path}; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`);
      }
    }
  });
  return cleared;
}

/** The async half: caches, service workers, databases. Never rejects. */
async function clearAsyncStores(deps: ResetSurfaces): Promise<void> {
  const jobs: Promise<unknown>[] = [];

  // Cache Storage. Nothing writes it today — there is no service worker — but
  // it is the one store that would survive every other kind of clearing, so a
  // button that claims to wipe the cache has to mean this one too.
  if (deps.caches) {
    const cs = deps.caches;
    jobs.push(cs.keys().then((keys) => Promise.all(keys.map((k) => cs.delete(k)))).catch(() => {}));
  }

  // A service worker outliving the build that installed it can serve stale
  // chunks forever. Unregistering does not kill the running one, but the next
  // load — which is one line away — comes from the network.
  if (deps.serviceWorker) {
    jobs.push(
      deps.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {}),
    );
  }

  // ⚠️ `databases()` IS NOT IN FIREFOX, so its absence is normal and not an
  // error. And a delete blocks on any other tab holding the database open,
  // which is why the whole of this function sits behind the deadline below.
  if (deps.indexedDB && typeof deps.indexedDB.databases === "function") {
    jobs.push(
      deps.indexedDB
        .databases()
        .then((dbs) =>
          Promise.all(
            dbs.map(
              (db) =>
                new Promise<void>((resolve) => {
                  if (!db.name) return resolve();
                  const req = deps.indexedDB!.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => resolve();
                }),
            ),
          ),
        )
        .catch(() => {}),
    );
  }

  await Promise.all(jobs);
}

/**
 * Throw away this browser's copy of the app. Resolves when done or when the
 * budget runs out, whichever is first — and never rejects.
 */
export async function hardReset(
  deps: ResetSurfaces = {},
  budgetMs = RESET_BUDGET_MS,
): Promise<void> {
  const surfaces: ResetSurfaces = {
    local: deps.local ?? (typeof localStorage === "undefined" ? null : localStorage),
    session: deps.session ?? (typeof sessionStorage === "undefined" ? null : sessionStorage),
    cookies:
      deps.cookies ??
      (typeof document === "undefined"
        ? null
        : { read: () => document.cookie, write: (v) => (document.cookie = v) }),
    pathname: deps.pathname ?? (typeof location === "undefined" ? "/" : location.pathname),
    caches: deps.caches ?? (typeof caches === "undefined" ? null : caches),
    serviceWorker:
      deps.serviceWorker ??
      (typeof navigator === "undefined" || !navigator.serviceWorker ? null : navigator.serviceWorker),
    indexedDB: deps.indexedDB ?? (typeof indexedDB === "undefined" ? null : indexedDB),
  };

  // SYNCHRONOUS FIRST, and it matters: these three are the stores the app
  // actually uses, they cannot block, and doing them before anything is awaited
  // means they are already gone even if the deadline below fires immediately.
  if (surfaces.local) attempt(() => surfaces.local!.clear());
  if (surfaces.session) attempt(() => surfaces.session!.clear());
  if (surfaces.cookies) clearVisibleCookies(surfaces.cookies, surfaces.pathname);

  // ⚠️ THE DEADLINE TIMER IS CLEARED, ALWAYS. `Promise.race` settles on the
  // first branch to finish and then simply forgets the other one — the timer it
  // left behind keeps running, and in the common case (the wipe finishing in
  // milliseconds) that is a live handle for the rest of the budget with nothing
  // waiting on it. In a browser it is a stray callback; under a test runner it
  // is a pending handle holding the event loop open after the test that made it
  // has finished, which is a whole category of "the suite hangs and the last
  // thing it printed is unrelated".
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      clearAsyncStores(surfaces).catch(() => {}),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, budgetMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * The whole button: wipe, then land on a fresh document at "/".
 *
 * "/" rather than a computed home route, because "/" is the one URL that
 * resolves the right landing page on the SERVER for whoever is signed in — and
 * a client that has just admitted it is in a bad state is the last thing that
 * should be deciding where its user belongs.
 *
 * `location.replace`, not `assign`: the entry being left is a crash, and it
 * must not be one Back button away.
 *
 * ⚠️ IT NAVIGATES EVEN IF THE WIPE FAILED. There is no error path here on
 * purpose — the promise made on screen is "put me back at work", and arriving
 * home with stale localStorage still keeps that promise. Arriving nowhere does
 * not.
 */
export async function resetAndGoHome(
  deps: ResetSurfaces & { location?: Pick<Location, "replace">; budgetMs?: number } = {},
): Promise<void> {
  try {
    await hardReset(deps, deps.budgetMs ?? RESET_BUDGET_MS);
  } catch {
    /* hardReset does not reject; this is here so a future edit cannot make it */
  }
  const loc = deps.location ?? (typeof location === "undefined" ? null : location);
  loc?.replace("/");
}
